import React, { useState, useEffect } from "react";
import { FaShoppingBag } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { fetchWishlist, removeFromWishlistAPI, clearWishlistAPI } from "../../redux/store/wishlist";
import { addToCart } from "../../redux/store/cartSlice";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { server } from "../../utilis/constant";

const Wishlist = () => {
  const { wishlistItems, loading, error } = useSelector((state) => state.wishlist);
  const { isLoggedIn } = useSelector((state) => state.user);
  const dispatch = useDispatch();

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(fetchWishlist());
    }
  }, [dispatch, isLoggedIn]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handleAddCart = (product) => {
    dispatch(addToCart(product.product));
    toast.success("Added to cart!", { autoClose: 2000 });
  };

  const handleRemove = (productId) => {
    dispatch(removeFromWishlistAPI(productId));
  };

  const handleClearWishlist = () => {
    if (window.confirm("Are you sure you want to clear your entire wishlist?")) {
      dispatch(clearWishlistAPI());
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 py-8 bg-gradient-to-b from-gray-50 to-white min-h-screen">
        <div className="text-center mt-16">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Please Login</h1>
          <p className="text-gray-600 mb-8">You need to be logged in to view your wishlist.</p>
          <a href="/login" className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition">
            Login Now
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>My Wish List</title>
        <meta name="description" content={`Welcome to My Wish List page`} />
      </Helmet>

      <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 py-8 bg-gradient-to-b from-gray-50 to-white min-h-screen">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-800">
            My Wish List
          </h1>
          <p className="text-gray-500 mt-2 text-sm sm:text-base">
            Your saved favorite products in one place
          </p>
        </div>

        {/* Top Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white/80 backdrop-blur-md border border-gray-100 rounded-2xl px-5 py-4 shadow-sm">
          <span className="text-gray-700 font-medium">
            ❤️ {wishlistItems.length} Item(s)
          </span>

          <div className="flex items-center gap-4">
            {wishlistItems.length > 0 && (
              <button
                onClick={handleClearWishlist}
                className="px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition text-sm"
              >
                Clear All
              </button>
            )}
            
            <div className="flex items-center gap-2">
              <label className="text-gray-600 text-sm">Show</label>
              <select className="px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-600 focus:outline-none focus:ring-2 focus:ring-black/10">
                <option value="10">10</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </select>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center mt-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your wishlist...</p>
          </div>
        )}
{console.log('Wishlist Items:', wishlistItems)}
        {/* Grid */}
        {!loading && (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-7">
            {wishlistItems && wishlistItems.length > 0 ? (
              wishlistItems
                .filter(item => item.product) // Filter out items with null/deleted products
                .map((item) => {
                const product = item.product;
                
                return (
                  <div
                    key={item._id}
                    className="group bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:-translate-y-1 flex flex-col"
                  >
                    {/* Image */}
                    <div className="h-[240px] bg-gradient-to-b from-gray-50 to-white flex items-center justify-center overflow-hidden">
                      {(product.image || product.variants?.[0]?.image) ? (
                        <img
                          src={`${server}/${product.image || product.variants?.[0]?.image}`}
                          alt={product.title}
                          className="h-full object-contain group-hover:scale-110 transition-transform duration-500"
                          onError={(e) => {
                            console.log('Image failed to load:', e.target.src);
                            console.log('Product structure:', product);
                            // Try fallback to first variant image if main image fails
                            if (product.variants?.[0]?.image && !e.target.src.includes(product.variants[0].image)) {
                              e.target.src = `${server}/${product.variants[0].image}`;
                            } else {
                              e.target.style.display = 'none';
                            }
                          }}
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full w-full bg-gray-200 text-gray-500">
                          <span>No Image</span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-5 flex flex-col flex-1 justify-between">
                      {/* Title */}
                      <h1 className="font-semibold text-lg text-gray-800 line-clamp-2">
                        {product.title}
                      </h1>

                      {/* Price */}
                      {product.variants && product.variants.length > 0 && (
                        <p className="text-lg font-bold text-gray-900 mt-2">
                          ${product.variants[0].price}
                        </p>
                      )}

                      {/* Actions */}
                      <div className="mt-5 gap-2 flex items-center justify-between">
                        <button
                          onClick={() => handleAddCart(item)}
                          className="flex items-center gap-2 bg-black text-white px-6 py-3 rounded-xl text-sm hover:bg-gray-800 active:scale-95 transition font-medium"
                        >
                          <FaShoppingBag size={16} />
                        </button>

                        <button
                          onClick={() => handleRemove(product._id)}
                          className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center hover:bg-red-200 hover:scale-110 transition"
                        >
                          <MdDelete color="red" size={20} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-span-full flex justify-center mt-16">
                <div className="text-center bg-white p-10 rounded-2xl shadow-sm border border-gray-100">
                  <p className="text-lg font-medium text-gray-700">
                    Your wishlist is empty 💔
                  </p>
                  <p className="text-gray-500 text-sm mt-2">
                    Start adding products you love
                  </p>
                  <a
                    href="/fragrance"
                    className="inline-block mt-4 bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition"
                  >
                    Browse Products
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default Wishlist;
